import { type ClassValue, clsx } from "clsx";
import { format, isBefore } from "date-fns";
import dayjs from "dayjs";
import type { DebouncedFunc } from "lodash.debounce";
import { extendTailwindMerge } from "tailwind-merge";
import { CALC_DECIMAL_PLACES, NUM_OF_DECIMAL_SHOWS } from "../constants";

/**
 * Extended tailwind-merge to recognize custom font sizes
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [
        {
          text: ["xxs", "xs", "sm", "md", "lg", "xl", "2xl", "3xl", "4xl"],
        },
      ],
    },
  },
});

/**
 * Combines class names using clsx and merges Tailwind classes
 * Extended to recognize custom font sizes like text-xxs
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Formats a number as currency
 */
export function formatCurrency(
  amount: number,
  currency: string = "AED",
  locale: string = "en-AE"
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  }).format(amount);
}

/**
 * Formats a date string
 */
export function formatDate(
  date: string | Date,
  locale: string = "en-AE",
  options?: Intl.DateTimeFormatOptions
): string {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...options,
  }).format(dateObj);
}


/**
 * Debounce function
 */
export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  wait: number
): DebouncedFunc<T> {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  const debouncedFn = (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      fn(...args);
    }, wait);
  };
  debouncedFn.cancel = () => {
    if (timeout) clearTimeout(timeout);
  };

  return debouncedFn as DebouncedFunc<T>;
}


/**
 * Storage utilities with type safety
 */
export const storage = {
  get<T>(key: string, defaultValue?: T): T | null {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue ?? null;
    } catch {
      return defaultValue ?? null;
    }
  },

  set<T>(key: string, value: T): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  },

  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error("Error removing from localStorage:", error);
    }
  },
};

/**
 * Generate unique ID
 */
export function generateId(): string {
  return Math.random().toString(36).substring(2, 9);
}

/**
 * Checks if a value is filled (not null, undefined, or empty string)
 */
function isFieldFilled(value: unknown): boolean {
  if (value === null || value === undefined || value === 0) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
}

/**
 * Calculates average progress percentage for multiple items
 * @param items - Array of objects to check
 * @param requiredFields - Array of field names to check
 * @returns Average progress percentage (0-100)
 * 
 * @example
 * calculateItemsProgress(draftItems, ['name_en', 'name_ar', 'base_uom', 'purchase_uom', 'pack_size', 'per_level'])
 */
export function calculateProgress<T extends Record<string, unknown>>(
  items: T[],
  requiredFields: string[]
): number {
  if (items.length === 0 || requiredFields.length === 0) return 0;

  const totalFilledCount = items.reduce((sum, item) => {
    return sum + requiredFields.filter(field => isFieldFilled(item[field])).length;
  }, 0);

  const totalPossibleFields = items.length * requiredFields.length;
  return Math.round((totalFilledCount / totalPossibleFields) * 100);
}

export function getFileName(url: string): string {
  try {
    const path = new URL(url, "https://dummy").pathname;
    return path.split("/").pop() || "document";
  } catch {
    return "document";
  }
}
export function formatAuditLogDate(created_at: string): string {
  if (!created_at || typeof created_at !== "string") return "—";
  const d = dayjs(created_at.trim());
  return d.isValid() ? d.format("MM/DD/YYYY") : "—";
}

/**
 * Converts a URL to a File object using the Vite proxy to avoid CORS issues
 * @param url - The URL of the file to fetch
 * @param filename - Optional filename. If not provided, will be extracted from URL
 * @returns Promise that resolves to a File object
 */
export async function urlToFile(
  url: string,
  filename?: string
): Promise<File> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.statusText}`);
    }

    const blob = await response.blob();
    const fileName =
      filename || getFileName(url) || `document-${Date.now()}.pdf`;

    return new File([blob], fileName, {
      type: blob.type || "application/pdf",
      lastModified: Date.now(),
    });
  } catch (error) {
    console.error("Error converting URL to File:", error);
    throw error;
  }
}

export const isPastDate = (dateString: string) => {
  try {
    return isBefore(new Date(dateString), new Date());
  } catch {
    return false;
  }
};
export const formatDateString = (dateString: string) => {
  try {
    return format(new Date(dateString), "dd/MM/yyyy");
  } catch {
    return dateString;
  }
};

/** Display quantity with unit, e.g. `45.000 kg`. */
export function formatQuantityWithUnit(
  value: number | string,
  unit: string,
): string {
  const trimmedUnit = unit.trim();
  return trimmedUnit ? `${formatPrice(value)} ${trimmedUnit}` : formatPrice(value);
}

/** Display requested / confirmed quantities with unit; `null`/`undefined` → em dash. */
export function formatRequestedConfirmedWithUnit(
  quantity: { requested: number; confirmed: number } | null | undefined,
  unit: string,
  emptyDisplay = "—",
): string {
  if (quantity == null) {
    return emptyDisplay;
  }
  const trimmedUnit = unit.trim();
  const confirmed = trimmedUnit
    ? `${formatPrice(quantity.confirmed)} ${trimmedUnit}`
    : formatPrice(quantity.confirmed);
  return `${formatPrice(quantity.requested)} / ${confirmed}`;
}

/**
 * Converts a stored/display price to a number.
 * Strips thousand separators first — parseFloat("1,000.000") is 1.
 */
export const parsePrice = (num: number | string | null | undefined): number => {
  if (num == null) return 0;
  if (typeof num === "number") {
    return Number.isFinite(num) ? num : 0;
  }

  const normalized = String(num).replace(/,/g, "").trim();
  if (normalized === "" || normalized === ".") return 0;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Truncates a number to the calculation precision (CALC_DECIMAL_PLACES = 4 decimals).
 * Use this for all intermediate calculation results to maintain consistent precision.
 * 
 * @example
 * calcTrunc(8.53647) // returns 8.5364
 * calcTrunc(10 / 3)  // returns 3.3333
 */
export const calcTrunc = (num: number | string): number => {
  const factor = Math.pow(10, CALC_DECIMAL_PLACES);
  // Stabilize float artifacts before trunc (e.g. 2.55 * 100 === 254.999...).
  // console.log("calcTrunc", Math.trunc(Number((parsePrice(num) * factor).toFixed(10))) / factor);
  return Math.trunc(Number((parsePrice(num) * factor).toFixed(10))) / factor;
};

/**
 * Formats a number for UI display with configurable decimal places.
 * Rounds to the specified decimals (standard rounding: 5+ rounds up).
 * Default decimals is NUM_OF_DECIMAL_SHOWS (3).
 * 
 * @example
 * formatPrice(2.5559)    // returns "2.556" (rounded)
 * formatPrice(8.5364)    // returns "8.536"
 * formatPrice(1234.5674) // returns "1,234.567"
 * formatPrice(8.5364, 2) // returns "8.54"
 */
export const formatPrice = (
  num: number | string,
  decimals = NUM_OF_DECIMAL_SHOWS
) =>
  parsePrice(num).toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

/**
 * Formats a number for inputs and stored values.
 * Same rounding as formatPrice, but without thousand separators
 * (browsers reject values like "1,000.000", and parseFloat stops at the comma).
 */
export const formatPriceInput = (
  num: number | string,
  decimals = NUM_OF_DECIMAL_SHOWS
) =>
  parsePrice(num).toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: false,
  });